import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Member } from '@/types/member';
import { logger } from '@/lib/logger';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
}

interface AuthUser extends User {
  isAdmin?: boolean;
  role?: string;
  memberData?: Member;
  subscriptionData?: SubscriptionData;
}

interface AuthState {
  user: AuthUser | null;
  member: Member | null;
  subscription: SubscriptionData;
  isLoading: boolean;
  isInitialized: boolean;
  isAdmin: boolean;
  isMember: boolean;
  hasAccess: boolean;
  accessLevel: 'none' | 'member' | 'admin' | 'super_admin';
  error: string | null;
}

export const useAuth = (requiredLevel: 'none' | 'member' | 'admin' | 'super_admin' = 'none') => {
  const navigate = useNavigate();
  
  const [state, setState] = useState<AuthState>({
    user: null,
    member: null,
    subscription: { subscribed: false },
    isLoading: true,
    isInitialized: false,
    isAdmin: false,
    isMember: false,
    hasAccess: false,
    accessLevel: 'none',
    error: null
  });

  // Cache pour éviter les re-vérifications (Phase 4 - Optimisation finale)
  const [userDataCache, setUserDataCache] = useState<{
    userId?: string;
    userData?: any;
    timestamp?: number;
  }>({});

  // Comprehensive user data fetching with SMART CACHE (Phase 4)
  const fetchUserData = useCallback(async (user: User) => {
    try {
      // Check cache first - 5 minute cache window
      const cacheKey = user.id;
      const cacheExpiry = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      
      if (userDataCache.userId === cacheKey && 
          userDataCache.userData && 
          userDataCache.timestamp && 
          (now - userDataCache.timestamp) < cacheExpiry) {
        console.log('✅ Using CACHED user data for:', user.email);
        return userDataCache.userData;
      }

      console.log('🔍 Fetching NEW user data for:', user.email);
      
      // Get user role and member data first (critical), subscription separately (non-blocking)
      const [roleResponse, memberResponse] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('club_members')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle()
      ]);

      const userRole = roleResponse.data?.role || 'member';
      const memberData = memberResponse.data;
      
      // Subscription check is non-blocking - don't let it break auth
      let subscriptionData = { subscribed: false };
      try {
        const subscriptionResponse = await supabase.functions.invoke('check-subscription');
        if (subscriptionResponse.data && !subscriptionResponse.error) {
          subscriptionData = subscriptionResponse.data;
        }
      } catch (subError) {
        console.warn('⚠️ Subscription check failed (non-blocking):', subError);
      }
      const isAdmin = userRole === 'admin' || userRole === 'super_admin';
      const isMember = !!memberData;

      const userData = {
        user: { ...user, isAdmin, role: userRole, memberData, subscriptionData },
        member: memberData,
        subscription: subscriptionData,
        isAdmin,
        isMember,
        accessLevel: userRole as AuthState['accessLevel']
      };

      // Update cache
      setUserDataCache({
        userId: cacheKey,
        userData,
        timestamp: now
      });

      console.log('✅ Complete user data fetched and CACHED:', { 
        userRole, 
        isAdmin, 
        isMember, 
        subscription: subscriptionData 
      });

      return userData;
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      throw error;
    }
  }, [userDataCache]);

  // Enhanced auth state update with timeout protection
  const updateAuthState = useCallback(async (session: Session | null) => {
    console.log('🔄 Updating auth state, session:', !!session);
    
    // Prevent unnecessary updates if we already have the same session
    if (state.isInitialized && session?.user?.id === state.user?.id && !state.isLoading) {
      console.log('⏭️ Skipping update - same user already loaded and stable');
      return;
    }
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    if (session?.user) {
      try {
        // Add timeout protection to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Auth timeout')), 10000)
        );
        
        const userData = await Promise.race([
          fetchUserData(session.user),
          timeoutPromise
        ]);
        
        const hasAccess = requiredLevel === 'none' || 
                         (requiredLevel === 'member' && userData.isMember) ||
                         (requiredLevel === 'admin' && userData.isAdmin);

        setState({
          ...userData,
          isLoading: false,
          isInitialized: true,
          hasAccess,
          error: null
        });

        // Handle redirect logic only on login
        handleAuthRedirect(userData, hasAccess);
      } catch (error) {
        console.error('❌ Failed to fetch user data:', error);
        logger.error('Auth state update failed:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitialized: true,
          error: error instanceof Error ? error.message : 'Failed to load user data',
          hasAccess: false
        }));
        
        // Show a retry option to user
        if (error instanceof Error && error.message === 'Auth timeout') {
          toast.error('Délai de connexion dépassé', {
            description: 'Cliquez pour réessayer',
            action: {
              label: 'Réessayer',
              onClick: () => updateAuthState(session)
            }
          });
        }
      }
    } else {
      setState({
        user: null,
        member: null,
        subscription: { subscribed: false },
        isLoading: false,
        isInitialized: true,
        isAdmin: false,
        isMember: false,
        hasAccess: requiredLevel === 'none',
        accessLevel: 'none',
        error: null
      });

      // Redirect if auth required
      if (requiredLevel !== 'none' && window.location.pathname !== '/member-access') {
        navigate('/member-access');
      }
    }
  }, [requiredLevel, fetchUserData, navigate, state.isInitialized, state.user?.id, state.isLoading]);

  // Simplified redirect handler
  const handleAuthRedirect = useCallback((userData: any, hasAccess: boolean) => {
    const currentPath = window.location.pathname;
    
    console.log('🔄 Handling redirect:', { currentPath, hasAccess, isMember: userData.isMember, isAdmin: userData.isAdmin });
    
    // Redirect from login page if authenticated
    if (currentPath === '/member-access' && userData.isMember) {
      const targetPath = userData.isAdmin ? '/admin' : '/members';
      navigate(targetPath, { replace: true });
      return;
    }

    // Handle access control
    if (!hasAccess && requiredLevel !== 'none') {
      if (userData.isMember) {
        navigate('/members', { replace: true });
      } else {
        navigate('/member-access', { replace: true });
      }
    }
  }, [navigate, requiredLevel]);

  useEffect(() => {
    console.log('🚀 Setting up auth listener');
    
    // Set up auth state listener FIRST - only trigger on actual auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔄 Auth state changed:', event);
        // ONLY update on real auth changes, not internal navigations
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          updateAuthState(session);
        }
      }
    );

    // Get initial session ONCE
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔍 Initial session check');
      updateAuthState(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // STABLE - no dependencies to prevent re-initialization

  const login = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        setState(prev => ({ ...prev, isLoading: false, error: error.message }));
        toast.error('Erreur de connexion', {
          description: error.message
        });
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Auth state change will handle the rest
        toast.success('Connexion réussie');
        return { success: true };
      }

      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Erreur inconnue' };
    } catch (error) {
      logger.error('Login error:', error);
      setState(prev => ({ ...prev, isLoading: false, error: 'Une erreur est survenue' }));
      toast.error('Erreur de connexion');
      return { success: false, error: 'Une erreur est survenue' };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
      toast.success('Déconnexion réussie');
    } catch (error) {
      logger.error('Logout error:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstname: firstName,
            lastname: lastName
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        setState(prev => ({ ...prev, isLoading: false, error: error.message }));
        toast.error('Erreur d\'inscription', {
          description: error.message
        });
        return { success: false, error: error.message };
      }

      if (data.user) {
        toast.success('Inscription réussie', {
          description: 'Vérifiez votre email pour confirmer votre compte'
        });
        setState(prev => ({ ...prev, isLoading: false }));
        return { success: true };
      }

      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Erreur inconnue' };
    } catch (error) {
      logger.error('Signup error:', error);
      setState(prev => ({ ...prev, isLoading: false, error: 'Une erreur est survenue' }));
      toast.error('Erreur d\'inscription');
      return { success: false, error: 'Une erreur est survenue' };
    }
  };

  const retryAuth = () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    supabase.auth.getSession().then(({ data: { session } }) => {
      updateAuthState(session);
    });
  };

  const refreshMemberData = async () => {
    if (!state.user?.id) return;
    
    try {
      console.log('🔄 Refreshing member data for user:', state.user.email);
      
      // Use the same fetchUserData to maintain consistency
      const userData = await fetchUserData(state.user);
      
      setState(prev => ({
        ...prev,
        ...userData
      }));
      
      return userData.member;
    } catch (error) {
      console.error('💥 Failed to refresh member data:', error);
      toast.error('Erreur lors de la mise à jour des données');
      throw error;
    }
  };

  return {
    ...state,
    login,
    logout,
    signup,
    retryAuth,
    refreshMemberData
  };
};